import { watch, type Ref } from "vue"
import { Mode } from "@/model/RootStore"
import { LayerType } from "@/model/DataModel.ts"
import type { useDetailPanelStore } from "@/stores/detailPanelStore"

type DetailPanelStore = ReturnType<typeof useDetailPanelStore>

type LayerDetailType =
	| "COMPLETED_ASSESSMENT"
	| "TOD"
	| "PPU"
	| "CORRIDOR"
	| "STRATEGIC"
	| "SOIL"
	| "TRANSIT_LINE"
	| "TRANSIT_STATION"
	| "ENDANGERED_SPECIES"
	| "ZONING"
	| "CITY_PV_DOCUMENT"

const layerIdByDetailType: Record<LayerDetailType, string> = {
	COMPLETED_ASSESSMENT: LayerType.CompletedAssessments,
	TOD: "planning-policy",
	PPU: "planning-policy",
	CORRIDOR: "planning-policy",
	STRATEGIC: "planning-policy",
	SOIL: "soil-capability",
	TRANSIT_LINE: "transit-lines",
	TRANSIT_STATION: "transit-stations",
	ENDANGERED_SPECIES: "endangered-species",
	ZONING: "zoning",
	CITY_PV_DOCUMENT: "city-pv-documents",
}

type LayerAwareStore = {
	filters: { layers: string[] }
	visibleLayers: string[]
	selectedOpportunityId: number | null
	selectedTransaction: unknown
	selectedParcels: unknown[]
	opportunities: { id: string; type: string }[]
}

type Params = {
	store: LayerAwareStore
	panelStore: DetailPanelStore
	mapRef: Ref<{ clearSelections?: () => void } | undefined>
	layerFeatureDetails: Ref<{ type: string }[]>
	mode: Ref<Mode>
	visibleOpportunityTypes: Ref<string[]>
}

export function useDetailPanelAutoClose({
	store,
	panelStore,
	mapRef,
	layerFeatureDetails,
	mode,
	visibleOpportunityTypes,
}: Params): void {
	if (layerFeatureDetails.value.length === 0 && panelStore.isOpen("layers")) {
		panelStore.close("layers")
	}

	// a table view has no map context for these panels to describe
	watch(
		mode,
		currentMode => {
			if (currentMode !== Mode.Table) return
			store.selectedOpportunityId = null
			store.selectedTransaction = null
			store.selectedParcels = []
			if (layerFeatureDetails.value.length > 0) layerFeatureDetails.value = []
			panelStore.close("opportunity")
			panelStore.close("transaction")
			panelStore.close("parcels")
			panelStore.close("layers")
		},
		{ immediate: true }
	)

	watch(
		[() => store.filters.layers, visibleOpportunityTypes, mode],
		([layers, opportunityTypes, currentMode]) => {
			if (currentMode === Mode.Table) return

			if (store.selectedOpportunityId != null) {
				const selected = store.opportunities.find(o => String(o.id) === String(store.selectedOpportunityId))
				if (selected && !opportunityTypes.includes(selected.type)) {
					store.selectedOpportunityId = null
					panelStore.close("opportunity")
				}
			}

			if (store.selectedTransaction != null && !layers.includes(LayerType.Transaction)) {
				store.selectedTransaction = null
			}
		},
		{ deep: true }
	)

	watch(
		[() => store.visibleLayers, mode],
		([layers, currentMode]) => {
			if (currentMode === Mode.Table) return

			if (store.selectedParcels.length > 0 && !layers.includes("cadastre")) {
				mapRef.value?.clearSelections?.()
			}

			if (layerFeatureDetails.value.length > 0) {
				const stillVisible = layerFeatureDetails.value.filter(detail =>
					layers.includes(layerIdByDetailType[detail.type as LayerDetailType])
				)
				if (stillVisible.length !== layerFeatureDetails.value.length) {
					layerFeatureDetails.value = stillVisible
					if (stillVisible.length === 0) panelStore.close("layers")
				}
			}
		},
		{ deep: true }
	)
}
